import type { Option } from "@waldner-laboreinrichtungen/component-system";
import type { MutableRefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAllDevices } from "../hooks/use-devices";
import { useIncidents } from "../hooks/use-incidents"; // Now using Zustand hook with pagination
import { ErrorHistory } from "../partials/error-history";
import { ErrorScreenWithText } from "../partials/error-screen";
import { LoadingScreenWithText } from "../partials/loading-screen";
import { PagePartial } from "../partials/page-partial";
import useOnScreen from "../util/use-on-screen";

export const ErrorHistoryPage = () => {
  const { t } = useTranslation();

  const { devices, error, isLoading } = useAllDevices();

  const statusOptions: (Option & { emptyText?: string })[] = [
    {
      id: 0,
      label: t("incident.status.all"),
      value: "all",
      emptyText: t("incident.empty.all") as string,
    },
    {
      id: 1,
      label: t("incident.status.active"),
      value: "open",
      emptyText: t("incident.empty.active") as string,
    },
    {
      id: 3,
      label: t("incident.state.reported"),
      value: "reported",
      emptyText: t("incident.empty.reported") as string,
    },
  ];

  const [selectedStatus, setSelectedStatus] = useState<
    Option & { emptyText?: string }
  >(statusOptions[0]);

  const activeFilter = selectedStatus.value === "open" ? true : undefined;
  const statusFilter =
    selectedStatus.value === "reported" ? ("REPORTED" as const) : undefined;

  const {
    error: incidentsError,
    incidents = [],
    isLoading: isLoadingIncidents,
    isLoadingMore = false,
    pagesTotal,
    setSize,
    size,
  } = useIncidents(activeFilter, statusFilter);

  const loadMoreComponent = useRef<
    MutableRefObject<HTMLTableRowElement>
  >() as unknown as MutableRefObject<HTMLTableRowElement>;

  const isVisible = useOnScreen(loadMoreComponent);
  const hasMore = !!pagesTotal && size < pagesTotal;

  useEffect(() => {
    if (isVisible && !isLoadingMore && hasMore) {
      setSize(size + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, isLoadingMore]);

  if (isLoading || isLoadingIncidents) return <LoadingScreenWithText />;

  // Inzident-Fetch-Fehler nicht verschlucken — sonst zeigt die Tabelle
  // "keine Fehler" statt des echten Fehlers.
  if (error || incidentsError)
    return <ErrorScreenWithText error={error || incidentsError} />;

  return (
    <PagePartial title={t("page.error_history")}>
      <ErrorHistory
        devices={devices}
        hasMore={hasMore}
        incidents={incidents}
        isLoadingMore={isLoadingMore}
        loadMoreComponent={loadMoreComponent}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
      />
    </PagePartial>
  );
};
